import React, { useMemo, useState } from 'react';
import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getGroupedRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';


function Chip({ children, style, onClick, title }) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '6px 10px',
        borderRadius: '999px',
        border: '1px solid #e5e7eb',
        background: '#fff',
        cursor: onClick ? 'pointer' : 'default',
        userSelect: 'none',
        fontSize: '12px',
        fontWeight: 600,
        color: '#111827',
        ...style,
      }}
    >
      {children}
    </button>
  );
}

function GroupingChip({ id, label, onRemove }) {
  const style = {
    background: '#ecfeff',
    border: '1px solid #06b6d4',
    cursor: 'default',
  };

  return (
    <div style={{ display: 'inline-block' }}>
      <Chip style={style} title="Clique para remover (use ▲▼ para ordenar)" onClick={onRemove}>
        🧲 {label} <span style={{ opacity: 0.7 }}>✕</span>
      </Chip>
    </div>
  );
}

function GroupingDropZone({ children, isOver, onDropId }) {
  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        try { e.dataTransfer.dropEffect = 'move'; } catch {}
      }}
      onDrop={(e) => {
        e.preventDefault();
        const id = e.dataTransfer.getData('text/plain');
        if (id) onDropId?.(id);
      }}
      style={{
        minHeight: 38,
        display: 'flex',
        gap: 8,
        flexWrap: 'wrap',
        alignItems: 'center',
        padding: 8,
        borderRadius: 12,
        border: `1px dashed ${isOver ? '#2563eb' : '#94a3b8'}`,
        background: isOver ? '#eff6ff' : '#f8fafc',
        flex: 1,
        transition: 'all .12s ease',
      }}
    >
      {children}
    </div>
  );
}

export default function ReportGridPro({
  title,
  columns,
  data,
  loading,
  defaultGrouping = [],
  initialSort = [],
  defaultColumnVisibility = {},
  storageKey,
}) {
  const [grouping, setGrouping] = useState(defaultGrouping);
  const [globalFilter, setGlobalFilter] = useState('');
  const [showColumnFilters, setShowColumnFilters] = useState(true);
  const [sorting, setSorting] = useState(initialSort);
  const [columnVisibility, setColumnVisibility] = useState(defaultColumnVisibility);
  const [showColumnsPanel, setShowColumnsPanel] = useState(false);
  const [dragOverGrouping, setDragOverGrouping] = useState(false);

  const persistedKey = useMemo(() => {
    if (!storageKey) return null;
    return `ReportGridPro:v1:${storageKey}`;
  }, [storageKey]);

  // Load persisted preferences (per report)
  React.useEffect(() => {
    if (!persistedKey) return;
    try {
      const raw = localStorage.getItem(persistedKey);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed.grouping)) setGrouping(parsed.grouping);
      if (parsed.columnVisibility && typeof parsed.columnVisibility === 'object') setColumnVisibility(parsed.columnVisibility);
      if (Array.isArray(parsed.sorting)) setSorting(parsed.sorting);
      if (typeof parsed.showColumnFilters === 'boolean') setShowColumnFilters(parsed.showColumnFilters);
    } catch {
      // ignore
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persistedKey]);

  // Persist preferences
  React.useEffect(() => {
    if (!persistedKey) return;
    try {
      const payload = {
        grouping,
        columnVisibility,
        sorting,
        showColumnFilters,
        savedAt: new Date().toISOString(),
      };
      localStorage.setItem(persistedKey, JSON.stringify(payload));
    } catch {
      // ignore
    }
  }, [persistedKey, grouping, columnVisibility, sorting, showColumnFilters]);

  const table = useReactTable({
    data: Array.isArray(data) ? data : [],
    columns,
    state: { grouping, globalFilter, sorting, columnVisibility },
    onGroupingChange: setGrouping,
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    globalFilterFn: (row, _columnId, filterValue) => {
      const q = (filterValue || '').toString().trim().toLowerCase();
      if (!q) return true;
      const raw = Object.values(row.original || {}).join(' ').toLowerCase();
      return raw.includes(q);
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    enableGrouping: true,
    enableExpanding: true,
    getRowCanExpand: (row) => (row.subRows?.length || 0) > 0,
  });

  const leafColumnIds = useMemo(() => {
    try {
      return table.getAllLeafColumns().map(c => c.id);
    } catch {
      return [];
    }
  }, [table]);

  const leafColumnIdsKey = useMemo(() => leafColumnIds.join('|'), [leafColumnIds]);

  const arrayEqual = (a, b) => {
    if (a === b) return true;
    if (!Array.isArray(a) || !Array.isArray(b)) return false;
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (a[i] !== b[i]) return false;
    }
    return true;
  };

  const groupableColumns = useMemo(() => {
    return table
      .getAllLeafColumns()
      .filter(c => c.getCanGroup?.() !== false)
      .map(c => ({ id: c.id, label: typeof c.columnDef.header === 'string' ? c.columnDef.header : c.id }));
  }, [table]);

  // Keep grouping/sorting aligned with current columns (protects against stale localStorage keys)
  // IMPORTANT: Only update state if something actually changed (avoids render loops).
  React.useEffect(() => {
    const valid = new Set(leafColumnIds);

    setGrouping(prev => {
      const next = Array.isArray(prev) ? prev.filter(id => valid.has(id)) : [];
      return arrayEqual(prev, next) ? prev : next;
    });

    setSorting(prev => {
      const next = Array.isArray(prev) ? prev.filter(s => valid.has(s?.id)) : [];
      return arrayEqual(prev, next) ? prev : next;
    });

    // columnVisibility: keep as-is; unknown keys are harmless.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leafColumnIdsKey]);

  const columnLabelById = useMemo(() => {
    const map = new Map();
    groupableColumns.forEach(c => map.set(c.id, c.label));
    return map;
  }, [groupableColumns]);

  const addGrouping = (colIdRaw) => {
    const colId = String(colIdRaw || '').trim();
    if (!colId) return;
    const valid = new Set(table.getAllLeafColumns().map(c => c.id));
    if (!valid.has(colId)) return;
    setGrouping(prev => (prev.includes(colId) ? prev : [...prev, colId]));
  };

  const beginHeaderDrag = (e, colId) => {
    try {
      e.dataTransfer.setData('text/plain', String(colId));
      e.dataTransfer.effectAllowed = 'move';
    } catch {}
  };

  const moveGroup = (id, dir) => {
    setGrouping(prev => {
      const idx = prev.indexOf(id);
      if (idx === -1) return prev;
      const nextIdx = idx + dir;
      if (nextIdx < 0 || nextIdx >= prev.length) return prev;
      const next = prev.slice();
      const tmp = next[idx];
      next[idx] = next[nextIdx];
      next[nextIdx] = tmp;
      return next;
    });
  };

  const exportCsv = () => {
    const visibleCols = table.getAllLeafColumns().filter(c => c.getIsVisible());
    const header = visibleCols.map(c => {
      const h = c.columnDef.header;
      return typeof h === 'string' ? h : c.id;
    });
    const rows = table.getRowModel().rows;

    const escape = (v) => {
      if (v === null || v === undefined) return '';
      const s = String(v);
      if (/[",\n]/.test(s)) return '"' + s.replace(/"/g, '""') + '"';
      return s;
    };

    const lines = [];
    lines.push(header.map(escape).join(','));
    rows.forEach(r => {
      if (r.getIsGrouped?.()) return;
      const vals = visibleCols.map(c => escape(r.getValue(c.id)));
      lines.push(vals.join(','));
    });

    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(title || 'relatorio').toLowerCase().replace(/\s+/g, '-')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
      <div style={{ padding: '14px 14px 10px 14px', borderBottom: '1px solid #eef2f7', background: '#fafafa' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 800, color: '#0f172a' }}>{title || 'Relatório'}</div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
              Para agrupar: arraste o cabeçalho da coluna (na tabela) para “Agrupar por”.
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
              placeholder="🔎 Pesquisa rápida..."
              style={{
                padding: '8px 10px',
                borderRadius: 10,
                border: '1px solid #e5e7eb',
                minWidth: 240,
                outline: 'none',
              }}
            />
            <button
              type="button"
              onClick={() => setShowColumnFilters(v => !v)}
              style={{ padding: '8px 10px', borderRadius: 10, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontWeight: 700 }}
            >
              {showColumnFilters ? '🔽 Filtros' : '🔼 Filtros'}
            </button>
            <button
              type="button"
              onClick={() => setShowColumnsPanel(v => !v)}
              style={{ padding: '8px 10px', borderRadius: 10, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontWeight: 700 }}
            >
              🧩 Colunas
            </button>
            <button
              type="button"
              style={{ padding: '8px 10px', borderRadius: 10, border: '1px solid #e5e7eb', background: '#fff', cursor: 'default', fontWeight: 700, color: '#64748b' }}
              title="Dica: arraste o cabeçalho de uma coluna para a zona 'Agrupar por'"
              disabled
            >
              🧲 Agrupar pelo cabeçalho
            </button>
            <button
              type="button"
              onClick={exportCsv}
              style={{ padding: '8px 10px', borderRadius: 10, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontWeight: 700 }}
            >
              ⬇️ CSV
            </button>

            {persistedKey && (
              <button
                type="button"
                onClick={() => {
                  try { localStorage.removeItem(persistedKey); } catch {}
                  setGrouping(defaultGrouping);
                  setSorting(initialSort);
                  setColumnVisibility(defaultColumnVisibility);
                  setShowColumnFilters(true);
                }}
                style={{ padding: '8px 10px', borderRadius: 10, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontWeight: 700 }}
                title="Limpar preferências guardadas (colunas/grupos/ordenação)"
              >
                ♻️ Reset
              </button>
            )}
          </div>
        </div>

        {showColumnsPanel && (
          <div style={{ marginTop: 10, padding: 10, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10 }}>
            <div style={{ fontWeight: 800, fontSize: 12, color: '#334155', marginBottom: 8 }}>Mostrar/ocultar colunas</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 6 }}>
              {table.getAllLeafColumns().map(col => (
                <label key={col.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#111827' }}>
                  <input type="checkbox" checked={col.getIsVisible()} onChange={col.getToggleVisibilityHandler()} />
                  <span>{typeof col.columnDef.header === 'string' ? col.columnDef.header : col.id}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        <div style={{ marginTop: 12 }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: '#334155' }}>Agrupar por:</div>
            <div
              style={{ flex: 1, display: 'flex' }}
              onDragEnter={() => setDragOverGrouping(true)}
              onDragLeave={() => setDragOverGrouping(false)}
              onDrop={() => setDragOverGrouping(false)}
            >
              <GroupingDropZone
                isOver={dragOverGrouping}
                onDropId={(id) => {
                  addGrouping(id);
                }}
              >
                {grouping.length === 0 ? (
                  <div style={{ fontSize: 12, color: '#64748b' }}>Arraste colunas aqui para agrupar…</div>
                ) : (
                  grouping.map((id, idx) => (
                    <div key={id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <GroupingChip
                        id={id}
                        label={columnLabelById.get(id) || id}
                        onRemove={() => setGrouping(prev => prev.filter(x => x !== id))}
                      />
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <button type="button" onClick={() => moveGroup(id, -1)} disabled={idx === 0} style={{ border: '1px solid #e5e7eb', background: '#fff', borderRadius: 6, width: 24, height: 18, cursor: 'pointer' }} title="Subir">▲</button>
                        <button type="button" onClick={() => moveGroup(id, +1)} disabled={idx === grouping.length - 1} style={{ border: '1px solid #e5e7eb', background: '#fff', borderRadius: 6, width: 24, height: 18, cursor: 'pointer' }} title="Descer">▼</button>
                      </div>
                    </div>
                  ))
                )}
              </GroupingDropZone>
            </div>
          </div>

        </div>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead style={{ background: '#fff' }}>
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => {
                  const canSort = header.column.getCanSort();
                  const sort = header.column.getIsSorted();
                  const canGroup = header.column.getCanGroup?.() !== false;
                  return (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      style={{
                        textAlign: 'left',
                        padding: '10px 10px',
                        borderBottom: '1px solid #e5e7eb',
                        fontSize: 12,
                        color: '#0f172a',
                        whiteSpace: 'nowrap',
                        cursor: canSort ? 'pointer' : 'default',
                        userSelect: 'none',
                      }}
                      draggable={canGroup}
                      onDragStart={canGroup ? (e) => beginHeaderDrag(e, header.column.id) : undefined}
                      title={canGroup ? "Arraste este cabeçalho para 'Agrupar por'" : undefined}
                      onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                    >
                      {header.isPlaceholder ? null : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontWeight: 800 }}>
                            {flexRender(header.column.columnDef.header, header.getContext())}
                          </span>
                          {sort === 'asc' && <span style={{ color: '#64748b' }}>▲</span>}
                          {sort === 'desc' && <span style={{ color: '#64748b' }}>▼</span>}
                        </div>
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}

            {showColumnFilters && (
              <tr>
                {table.getHeaderGroups()[0].headers.map(header => {
                  const col = header.column;
                  const canFilter = col.getCanFilter?.() !== false;
                  return (
                    <th key={header.id} style={{ padding: '6px 10px', borderBottom: '1px solid #f1f5f9' }}>
                      {canFilter ? (
                        <input
                          value={(col.getFilterValue?.() ?? '')}
                          onChange={(e) => col.setFilterValue?.(e.target.value)}
                          placeholder="filtrar…"
                          style={{ width: '100%', padding: '6px 8px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 12 }}
                        />
                      ) : null}
                    </th>
                  );
                })}
              </tr>
            )}
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={table.getAllLeafColumns().length} style={{ padding: 18, textAlign: 'center', color: '#64748b' }}>
                  ⏳ A carregar…
                </td>
              </tr>
            ) : table.getRowModel().rows.length === 0 ? (
              <tr>
                <td colSpan={table.getAllLeafColumns().length} style={{ padding: 18, textAlign: 'center', color: '#64748b' }}>
                  Sem dados para o período/filtros.
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map(row => (
                <tr key={row.id} style={{ borderBottom: '1px solid #f1f5f9', background: row.getIsGrouped?.() ? '#f8fafc' : '#fff' }}>
                  {row.getVisibleCells().map(cell => {
                    const isGrouped = cell.getIsGrouped?.();
                    const isAggregated = cell.getIsAggregated?.();
                    const isPlaceholder = cell.getIsPlaceholder?.();

                    return (
                      <td key={cell.id} style={{ padding: '9px 10px', fontSize: 12, color: '#0f172a', verticalAlign: 'top' }}>
                        {isPlaceholder ? null : isGrouped ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <button
                              type="button"
                              onClick={row.getToggleExpandedHandler()}
                              style={{
                                width: 24,
                                height: 24,
                                borderRadius: 8,
                                border: '1px solid #e5e7eb',
                                background: '#fff',
                                cursor: 'pointer',
                                fontWeight: 900,
                              }}
                              title={row.getIsExpanded() ? 'Recolher' : 'Expandir'}
                            >
                              {row.getIsExpanded() ? '−' : '+'}
                            </button>
                            <span style={{ fontWeight: 900 }}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</span>
                            <span style={{ color: '#64748b' }}>({row.subRows?.length || 0})</span>
                          </div>
                        ) : isAggregated ? (
                          <span style={{ fontWeight: 800, color: '#334155' }}>
                            {flexRender(cell.column.columnDef.aggregatedCell ?? cell.column.columnDef.cell, cell.getContext())}
                          </span>
                        ) : (
                          flexRender(cell.column.columnDef.cell, cell.getContext())
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
